import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Redirect, useLocation, useSearch } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import type { Log } from "@shared/schema";
import { MOODS, SECONDARY_MOODS, CONTEXT_TAGS, MOOD_DESCRIPTIONS, type Mood } from "@shared/schema";
import {
  Pencil, X, Check, MessageCircle, PenLine, Lightbulb, Target, Sparkles,
  TrendingUp, TrendingDown, Minus, RefreshCw, Quote, Hash, Layers,
  ChevronDown, ArrowLeft, ImagePlus, Trash2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { MirrorCard } from "@/components/MirrorCard";
import { AtmosphericPage } from "@/components/MirrorAtmosphere";
import { formatTypographicQuotes } from "@/lib/typographic-quotes";

function getMoodDescription(mood: string): string {
  return MOOD_DESCRIPTIONS[mood as Mood] ?? "";
}

interface ImpactArea {
  area: string;
  intensity: number;
  direction: "worsening" | "stable" | "improving";
}

function DirectionIcon({ direction }: { direction: string }) {
  if (direction === "improving") return <TrendingUp size={11} style={{ color: "var(--accent-text-soft)" }} />;
  if (direction === "worsening") return <TrendingDown size={11} style={{ color: "var(--accent-text-soft)" }} />;
  return <Minus size={11} style={{ color: "var(--text-muted)" }} />;
}

function directionColor(direction: string): string {
  if (direction === "improving") return "var(--accent-text-soft)";
  if (direction === "worsening") return "var(--accent-text-soft)";
  return "var(--text-muted)";
}

function IntensityDots({ value, color }: { value: number; color: string }) {
  return (
    <div className="flex gap-0.5 items-center">
      {Array.from({ length: 10 }, (_, i) => (
        <div
          key={i}
          className="rounded-full"
          style={{
            width: "4px",
            height: i < value ? "10px" : "4px",
            backgroundColor: i < value ? color : "var(--border-hairline)",
            transition: "all 0.3s ease",
          }}
        />
      ))}
    </div>
  );
}

const FALLBACK_TIME_ZONE = "UTC";

function resolveTimeZone(timeZone: string | null | undefined): string {
  const candidate = timeZone?.trim() || FALLBACK_TIME_ZONE;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: candidate }).format();
    return candidate;
  } catch {
    console.error(`Invalid configured timezone "${candidate}"; using ${FALLBACK_TIME_ZONE}.`);
    return FALLBACK_TIME_ZONE;
  }
}

function formatTime(date: Date, timeZone: string): string {
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  }).toLowerCase();
}

function formatDateKey(date: Date | string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  }).formatToParts(new Date(date));
  const getPart = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find(part => part.type === type)?.value;
  const year = getPart("year");
  const month = getPart("month");
  const day = getPart("day");
  if (!year || !month || !day) {
    throw new Error(`Unable to format date key for timezone ${timeZone}.`);
  }
  return `${year}-${month}-${day}`;
}

function formatDateKeyLabel(
  dateKey: string,
  options: Intl.DateTimeFormatOptions,
): string {
  return new Date(`${dateKey}T12:00:00.000Z`).toLocaleDateString("en-US", {
    ...options,
    timeZone: "UTC",
  });
}

function previousDateKey(dateKey: string): string {
  const date = new Date(`${dateKey}T12:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

function isDateKey(value: string | null): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  return new Date(`${value}T12:00:00.000Z`).toISOString().slice(0, 10) === value;
}

function EntryPreview({ log, onClick, timeZone }: { log: Log; onClick: () => void; timeZone: string }) {
  const color = "var(--accent-color)";
  const time = formatTime(new Date(log.loggedAt), timeZone);
  const isConversation = log.source === "conversation";
  const brief = log.moment
    ? log.moment.length > 100 ? log.moment.slice(0, 100).trim() + "..." : log.moment
    : log.summary
      ? log.summary.length > 100 ? log.summary.slice(0, 100).trim() + "..." : log.summary
      : "";

  return (
    <button
      className="w-full text-left px-5 py-3.5 transition-colors duration-150"
      style={{ borderBottom: "1px solid var(--border-hairline)" }}
      onClick={onClick}
      data-testid={`button-entry-preview-${log.id}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <div
          className="w-1.5 h-1.5 rounded-full shrink-0"
          style={{ backgroundColor: color, opacity: 0.8 }}
          data-testid={`entry-preview-mood-dot-${log.id}`}
        />
        <span
          style={{ color, fontFamily: "var(--font-mono)", fontSize: "var(--type-label-size)", fontWeight: 500, letterSpacing: "var(--type-label-tracking)", textTransform: "uppercase" }}
        >
          {log.mood}
        </span>
        {log.secondaryMood && (
          <span style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: "var(--type-label-size)", fontWeight: 500, letterSpacing: "var(--type-label-tracking)", textTransform: "uppercase" }}>
            / {log.secondaryMood}
          </span>
        )}
        <span className="ml-auto flex items-center gap-1.5 font-mono" style={{ color: "var(--text-muted)" }}>
          {isConversation && <MessageCircle size={10} style={{ color: "var(--accent-text-soft)" }} />}
          {time}
        </span>
      </div>
      {brief && (
        <p
          className="leading-[1.7]"
          style={{ color: "var(--text-secondary)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem" }}
        >
          {brief}
        </p>
      )}
    </button>
  );
}

function EntryDetail({ log, dateKey, timeZone, onBack, onSave, isSaving }: {
  log: Log;
  dateKey: string;
  timeZone: string;
  onBack: () => void;
  onSave: (id: number, data: any) => void;
  isSaving: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const { toast } = useToast();
  const [editData, setEditData] = useState({
    mood: log.mood,
    moodIntensity: log.moodIntensity,
    secondaryMood: log.secondaryMood || "",
    energyLevel: log.energyLevel ?? 5,
    sleepQuality: log.sleepQuality ?? 5,
    tags: Array.isArray(log.tags) ? [...log.tags] : [],
    moment: log.moment,
  });

  const photos: string[] = Array.isArray((log as any).photos) ? (log as any).photos.filter(Boolean) : [];

  const handleAddPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Please select an image file", variant: "destructive" });
      return;
    }
    if (file.size > 1.5 * 1024 * 1024) {
      toast({ title: "Image too large. Please use one under 1.5MB.", variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const newPhotos = [...photos, reader.result as string];
      onSave(log.id, { photos: newPhotos });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleRemovePhoto = (index: number) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    onSave(log.id, { photos: newPhotos });
  };

  const handleSave = () => {
    onSave(log.id, {
      mood: editData.mood,
      moodIntensity: editData.moodIntensity,
      secondaryMood: editData.secondaryMood || null,
      energyLevel: editData.energyLevel,
      sleepQuality: editData.sleepQuality,
      tags: editData.tags,
      moment: editData.moment,
    });
    setEditing(false);
  };

  const handleCancel = () => {
    setEditData({
      mood: log.mood,
      moodIntensity: log.moodIntensity,
      secondaryMood: log.secondaryMood || "",
      energyLevel: log.energyLevel ?? 5,
      sleepQuality: log.sleepQuality ?? 5,
      tags: Array.isArray(log.tags) ? [...log.tags] : [],
      moment: log.moment,
    });
    setEditing(false);
  };

  const toggleTag = (tag: string) => {
    setEditData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag],
    }));
  };

  const isConversation = log.source === "conversation";
  const time = formatTime(new Date(log.loggedAt), timeZone);
  const color = "var(--accent-color)";
  const moodDesc = getMoodDescription(log.mood);

  const takeaways: string[] = Array.isArray(log.takeaways) ? log.takeaways.filter(Boolean) : [];
  const rawImpact = Array.isArray(log.impactAreas) ? log.impactAreas : [];
  const impactAreas: ImpactArea[] = rawImpact.map((a: any) => ({
    area: String(a?.area || "Unknown"),
    intensity: Math.max(1, Math.min(10, Number(a?.intensity) || 5)),
    direction: (["worsening", "stable", "improving"].includes(a?.direction) ? a.direction : "stable") as ImpactArea["direction"],
  }));
  const solutionsDiscussed: string[] = Array.isArray(log.solutionsDiscussed) ? log.solutionsDiscussed.filter(s => Boolean(s) && !s.startsWith("[object")) : [];
  const reflectionQuote: string | null = log.reflectionQuote || null;
  const themes: string[] = Array.isArray(log.themes) ? log.themes.filter(Boolean) : [];
  const keyWords: string[] = Array.isArray(log.keyWords) ? log.keyWords.filter(Boolean) : [];
  const summary: string | null = log.summary || null;

  const dateLabel = formatDateKeyLabel(dateKey, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="fade-in">
      <button
        onClick={onBack}
        className="touch-target flex items-center gap-2 mb-4 px-1 py-1 transition-opacity duration-150"
        data-testid="button-back-to-day"
      >
        <ArrowLeft size={14} style={{ color: "var(--text-muted)" }} />
        <span style={{ color: "var(--text-muted)", fontFamily: "var(--font-display)" }}>
          Back to {dateLabel}
        </span>
      </button>

      <div
        className="rounded-xl overflow-hidden"
        style={{
          background: "var(--surface-1)",
          border: `1px solid ${editing ? "var(--accent-border)" : "var(--border-hairline)"}`,
        }}
        data-testid={`entry-detail-${log.id}`}
      >
        <div className="px-6 pt-5 pb-2">
          <div className="flex items-start justify-between mb-1.5">
            <div className="flex items-center gap-2.5">
              <div
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: color, opacity: 0.85, boxShadow: "0 0 12px var(--accent-glow)" }}
                data-testid={`entry-detail-mood-dot-${log.id}`}
              />
              <span
                className="font-display text-xl"
                style={{ color, fontWeight: 500, lineHeight: 1.2 }}
                data-testid={`text-entry-mood-${log.id}`}
              >
                {log.mood.charAt(0).toUpperCase() + log.mood.slice(1)}
              </span>
              {log.secondaryMood && (
                <>
                  <span style={{ color: "var(--border-medium)" }}>/</span>
                  <span className="font-display text-base text-mirror-text-muted">
                    {log.secondaryMood}
                  </span>
                </>
              )}
            </div>
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="touch-target opacity-60 hover:opacity-100 transition-opacity p-1 rounded"
                data-testid={`button-edit-entry-${log.id}`}
              >
                <Pencil size={12} style={{ color: "var(--fg-raw)" }} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-1.5 mb-3">
            {isConversation ? (
              <MessageCircle size={10} style={{ color: "var(--accent-text-soft)" }} />
            ) : (
              <PenLine size={10} className="text-mirror-text-muted" />
            )}
            <span style={{ fontSize: "var(--type-label-size)", color: "var(--text-muted)", fontFamily: "var(--font-mono)", letterSpacing: "var(--type-label-tracking)", textTransform: "uppercase" }}>
              {time}
            </span>
          </div>

          {moodDesc && (
            <p className="mb-3" style={{ fontSize: "0.9375rem", fontWeight: 400, color: "var(--text-muted)", fontFamily: "var(--font-reflective)", fontStyle: "italic" }}>
              {moodDesc}
            </p>
          )}

          {log.moment && !editing && (
            <p
              className="text-sm leading-relaxed mb-4"
              style={{ color: "var(--text-secondary)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400, lineHeight: "1.8" }}
              data-testid={`text-entry-moment-${log.id}`}
            >
              {log.moment}
            </p>
          )}

          {photos.length > 0 && (
            <div className="flex gap-2 flex-wrap mb-3">
              {photos.map((photo, i) => (
                <div key={i} className="relative group/photo">
                  <img
                    src={photo}
                    alt=""
                    className="w-20 h-20 rounded-lg object-cover"
                    style={{ border: "1px solid var(--border-medium)" }}
                    data-testid={`img-entry-photo-${log.id}-${i}`}
                  />
                  <button
                    onClick={() => handleRemovePhoto(i)}
                    className="touch-target absolute -top-4 -right-4 rounded-full flex items-center justify-center bg-transparent opacity-0 group-hover/photo:opacity-100 transition-opacity"
                    data-testid={`button-remove-photo-${log.id}-${i}`}
                  >
                    <span
                      className="flex h-5 w-5 items-center justify-center rounded-full"
                      style={{ background: "var(--status-error-strong)" }}
                    >
                      <X size={10} style={{ color: "var(--status-on-error)" }} />
                    </span>
                  </button>
                </div>
              ))}
            </div>
          )}
          {!editing && photos.length < 5 && (
            <label
              className="touch-target inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg cursor-pointer transition-opacity opacity-60 hover:opacity-100 mb-3"
              style={{
                border: "1px dashed var(--border-medium)",
                color: "var(--text-muted)",
                fontFamily: "var(--font-display)",
                fontSize: "0.75rem",
                fontWeight: 500,
              }}
              data-testid={`button-add-photo-${log.id}`}
            >
              <ImagePlus size={12} />
              Add photo
              <input type="file" accept="image/*" className="hidden" onChange={handleAddPhoto} />
            </label>
          )}
        </div>

        {editing && (
          <div className="px-6 pb-5 space-y-4 pt-3" onClick={(e) => e.stopPropagation()}>
            <div style={{ borderTop: "1px solid var(--surface-3)" }}>
              <div className="flex items-center justify-end gap-1 mb-2 opacity-40 hover:opacity-80 transition-opacity p-1.5 rounded">
                <button className="touch-target" onClick={handleCancel} data-testid={`button-cancel-edit-${log.id}`}>
                  <X size={14} style={{ color: "var(--fg-raw)" }} />
                </button>
                <button onClick={handleSave} disabled={isSaving} className="touch-target opacity-60 hover:opacity-100 transition-opacity p-1.5 rounded disabled:opacity-60" data-testid={`button-save-edit-${log.id}`}>
                  <Check size={14} style={{ color: "var(--accent-color)" }} />
                </button>
              </div>
              <div>
                <label className="text-xs mb-2 block font-mono flex flex-wrap gap-1.5">MOOD</label>
                <div>
                  {MOODS.map(m => (
                    <button key={m} onClick={() => setEditData(prev => ({ ...prev, mood: m }))} data-testid={`button-mood-${m}`} className="touch-target px-2.5 py-1 text-xs rounded-md transition-all" style={{ background: editData.mood === m ? "var(--accent-bg)" : "transparent", border: `1px solid ${editData.mood === m ? "var(--accent-border)" : "var(--border-hairline)"}`, color: editData.mood === m ? "var(--accent-color)" : "var(--text-secondary)", fontFamily: "var(--font-display)", fontSize: "0.75rem", fontWeight: 500, textTransform: "uppercase" }}>{m}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs mb-2 block font-mono flex flex-wrap gap-1.5">UNDERTONE</label>
                <div>
                  <button onClick={() => setEditData(prev => ({ ...prev, secondaryMood: "" }))} className="touch-target px-2.5 py-1 text-xs rounded-md" style={{ background: !editData.secondaryMood ? "var(--border-hairline)" : "transparent", border: "1px solid var(--border-medium)", fontFamily: "var(--font-display)", fontSize: "0.75rem", fontWeight: 500, textTransform: "uppercase" }}>none</button>
                  {SECONDARY_MOODS.map(m => (
                    <button key={m} onClick={() => setEditData(prev => ({ ...prev, secondaryMood: m }))} data-testid={`button-secondary-${m}`} className="touch-target px-2.5 py-1 text-xs rounded-md transition-all" style={{ background: editData.secondaryMood === m ? "var(--border-light)" : "transparent", border: `1px solid ${editData.secondaryMood === m ? "var(--border-strong)" : "var(--border-hairline)"}`, color: editData.secondaryMood === m ? "var(--text-secondary)" : "var(--text-muted)", fontFamily: "var(--font-display)", fontSize: "0.75rem", fontWeight: 500, textTransform: "uppercase" }}>{m}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs mb-2 block font-mono flex flex-wrap gap-1.5">CONTEXT</label>
                <div>
                  {CONTEXT_TAGS.map(tag => (
                    <button key={tag} onClick={() => toggleTag(tag)} data-testid={`button-tag-${tag}`} className="touch-target px-2.5 py-1 text-xs rounded-md transition-all" style={{ background: editData.tags.includes(tag) ? "var(--border-light)" : "transparent", border: `1px solid ${editData.tags.includes(tag) ? "var(--border-strong)" : "var(--border-hairline)"}`, color: editData.tags.includes(tag) ? "var(--text-secondary)" : "var(--text-muted)", fontFamily: "var(--font-display)", fontSize: "0.75rem", fontWeight: 500, textTransform: "uppercase" }}>{tag}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs mb-1.5 block font-mono">INTENSITY</label>
                  <input type="range" min={0} max={1} step={0.05} value={editData.moodIntensity} onChange={e => setEditData(prev => ({ ...prev, moodIntensity: parseFloat(e.target.value) }))} className="w-full accent-[var(--accent-color)]" data-testid="input-edit-intensity" style={{ height: "4px" }} />
                  <div className="text-xs text-center mt-1 text-mirror-text-muted" style={{ fontFamily: "var(--font-display)" }}>{Math.round(editData.moodIntensity * 100)}%</div>
                </div>
                <div>
                  <label className="text-xs mb-1.5 block font-mono">ENERGY</label>
                  <input type="range" min={1} max={10} value={editData.energyLevel} onChange={e => setEditData(prev => ({ ...prev, energyLevel: parseInt(e.target.value) }))} className="w-full accent-[var(--accent-color)]" data-testid="input-edit-energy" style={{ height: "4px" }} />
                  <div className="text-xs text-center mt-1 text-mirror-text-muted" style={{ fontFamily: "var(--font-display)" }}>{editData.energyLevel}/10</div>
                </div>
                <div>
                  <label className="text-xs mb-1.5 block font-mono">SLEEP</label>
                  <input type="range" min={1} max={10} value={editData.sleepQuality} onChange={e => setEditData(prev => ({ ...prev, sleepQuality: parseInt(e.target.value) }))} className="w-full accent-[var(--accent-color)]" data-testid="input-edit-sleep" style={{ height: "4px" }} />
                  <div className="text-xs text-center mt-1 text-mirror-text-muted" style={{ fontFamily: "var(--font-display)" }}>{editData.sleepQuality}/10</div>
                </div>
              </div>
              <div>
                <label className="text-xs mb-1.5 block font-mono">MOMENT</label>
                <textarea value={editData.moment} onChange={e => setEditData(prev => ({ ...prev, moment: e.target.value }))} className="w-full bg-transparent resize-none outline-none px-3 py-2 placeholder:italic placeholder:opacity-30" data-testid={`input-edit-moment-${log.id}`} rows={2} style={{ color: "var(--fg-raw)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400, border: "1px solid var(--border-light)", borderRadius: "8px", background: "var(--surface-1)" }} />
              </div>
            </div>
          </div>
        )}

        {!editing && (summary || takeaways.length > 0 || impactAreas.length > 0 || reflectionQuote || themes.length > 0 || keyWords.length > 0 || solutionsDiscussed.length > 0) && (
          <div className="px-6 pb-5 space-y-4">
            {summary && (
              <div
                className="py-3 px-4"
                style={{
                  background: "var(--surface-1)",
                  borderRadius: "12px",
                  borderLeft: "3px solid var(--accent-border)",
                }}
                data-testid={`text-entry-summary-${log.id}`}
              >
                <p
                  className="leading-relaxed italic"
                  style={{ fontSize: "0.9375rem", fontWeight: 400, lineHeight: "1.75", color: "var(--text-secondary)", fontFamily: "var(--font-reflective)" }}
                >
                  {formatTypographicQuotes(summary)}
                </p>
              </div>
            )}

            {impactAreas.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2.5">
                  <Target size={11} style={{ color: "var(--accent-text-soft)" }} />
                  <span className="font-mono" style={{ color: "var(--accent-text-soft)" }}>
                    WHAT'S IMPACTING YOU
                  </span>
                </div>
                <div className="space-y-2 ml-4">
                  {impactAreas.map((area, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm" style={{ fontFamily: "var(--font-display)" }} data-testid={`impact-area-${log.id}-${i}`}>
                      <span style={{ minWidth: "130px", fontWeight: 500 }}>
                        {formatTypographicQuotes(area.area)}
                      </span>
                      <IntensityDots value={area.intensity} color="var(--accent-text-soft)" />
                      <DirectionIcon direction={area.direction} />
                      <span className="font-mono" style={{ color: directionColor(area.direction) }}>
                        {area.direction}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {takeaways.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2.5">
                  <Lightbulb size={11} style={{ color: "var(--accent-text-soft)" }} />
                  <span className="font-mono" style={{ color: "var(--accent-text-soft)" }}>
                    KEY TAKEAWAYS
                  </span>
                </div>
                <div className="space-y-2 ml-4">
                  {takeaways.map((t, i) => (
                    <div key={i} className="flex items-start gap-2 shrink-0 mt-0.5" data-testid={`text-takeaway-${log.id}-${i}`}>
                      <span style={{ color: "var(--accent-text-soft)", fontSize: "var(--type-label-size)", fontFamily: "var(--font-display)" }}>{i + 1}.</span>
                      <p className="leading-relaxed" style={{ color: "var(--text-secondary)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400, lineHeight: "1.7" }}>
                        {formatTypographicQuotes(t)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {solutionsDiscussed.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2.5">
                  <Sparkles size={11} style={{ color: "var(--accent-text-soft)" }} />
                  <span className="font-mono" style={{ color: "var(--accent-text-soft)" }}>
                    SOLUTIONS EXPLORED
                  </span>
                </div>
                <div className="space-y-2 ml-4">
                  {solutionsDiscussed.map((s, i) => (
                    <div key={i} className="flex items-start gap-2" data-testid={`solution-${log.id}-${i}`}>
                      <span style={{ color: "var(--accent-text-soft)", marginTop: "2px", fontSize: "var(--type-label-size)" }}>→</span>
                      <p className="leading-relaxed" style={{ color: "var(--text-secondary)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400, lineHeight: "1.7" }}>
                        {formatTypographicQuotes(s)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {themes.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2.5">
                  <Layers size={11} style={{ color: "var(--accent-text-soft)" }} />
                  <span className="font-mono" style={{ color: "var(--accent-text-soft)" }}>
                    THEMES
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 ml-4">
                  {themes.map((theme, i) => (
                    <span
                      key={i}
                      className="px-2.5 py-1 font-mono text-xs"
                      style={{
                        color: "var(--text-muted)",
                        background: "var(--accent-bg)",
                        border: "1px solid var(--accent-border)",
                        borderRadius: "8px",
                        fontWeight: 500,
                      }}
                      data-testid={`theme-${log.id}-${i}`}
                    >
                      {formatTypographicQuotes(theme)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {keyWords.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Hash size={11} style={{ color: "var(--text-muted)" }} />
                  <span className="font-mono" style={{ color: "var(--text-muted)" }}>
                    KEY WORDS
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 ml-4">
                  {keyWords.map((word, i) => (
                    <span
                      key={i}
                      className="font-mono"
                      style={{ color: "var(--text-muted)" }}
                      data-testid={`keyword-${log.id}-${i}`}
                    >
                      {formatTypographicQuotes(word)}{i < keyWords.length - 1 && <span style={{ color: "var(--border-medium)", margin: "0 4px" }}>·</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {reflectionQuote && (
              <div
                className="mt-2 py-3 px-4 flex items-start gap-3"
                style={{
                  background: "var(--surface-1)",
                  borderRadius: "12px",
                  border: "1px solid var(--accent-border)",
                }}
                data-testid={`quote-${log.id}`}
              >
                <Quote size={14} className="shrink-0 mt-0.5 font-display italic text-sm" style={{ color: "var(--accent-text-soft)", transform: "scaleX(-1)" }} />
                <p
                  style={{ color: "var(--text-secondary)", lineHeight: "1.7", fontWeight: 400, fontSize: "0.9375rem", fontFamily: "var(--font-reflective)" }}
                >
                  {formatTypographicQuotes(reflectionQuote)}
                </p>
              </div>
            )}

            <div className="flex items-center gap-4 pt-1 flex-wrap">
              {(Array.isArray(log.tags) ? log.tags : []).map(tag => (
                <span key={tag} className="px-2 py-0.5 font-mono" style={{ background: "var(--surface-3)", borderRadius: "6px" }} data-testid={`tag-${log.id}-${tag}`}>
                  {tag}
                </span>
              ))}
              <div className="flex items-center gap-3 ml-auto">
                {log.energyLevel != null && (
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono">energy</span>
                    <IntensityDots value={log.energyLevel} color="var(--accent-text-soft)" />
                  </div>
                )}
                {log.sleepQuality != null && (
                  <div className="flex items-center gap-1.5 font-mono">
                    <span>sleep</span>
                    <IntensityDots value={log.sleepQuality} color="var(--accent-text-soft)" />
                  </div>
                )}
                <span style={{ fontSize: "var(--type-label-size)", fontFamily: "var(--font-display)" }}>
                  {Math.round(log.moodIntensity * 100)}%
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface TimelineProps {
  entryId?: string;
  timeZone: string | null;
}

export default function Timeline({ entryId, timeZone }: TimelineProps) {
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const search = useSearch();
  const configuredTimeZone = resolveTimeZone(timeZone);
  const searchParams = new URLSearchParams(search);
  const requestedDay = searchParams.get("day");
  const requestedFromDay = searchParams.get("from");
  const initialExpandedDay = isDateKey(requestedDay) ? requestedDay : null;
  const [enriching, setEnriching] = useState(false);
  const [enrichTrigger, setEnrichTrigger] = useState(0);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(
    () => new Set(initialExpandedDay ? [initialExpandedDay] : []),
  );

  const {
    data: logs = [],
    isLoading,
    isError,
    refetch,
  } = useQuery<Log[]>({
    queryKey: ["/api/logs"],
    refetchInterval: query => {
      const data = query.state.data as Log[] | undefined;
      return data?.some(log => (
        log.directAddressed === false
        && Boolean(log.summary || log.reflectionQuote || log.takeaways?.length || log.solutionsDiscussed?.length)
      )) ? 1500 : false;
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      apiRequest("PATCH", `/api/logs/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/logs"] });
    },
  });

  const needsEnrichment = logs.some(l => l.moment && l.moment.length > 10 && (!l.reflectionQuote || !l.themes || l.themes.length === 0));

  useEffect(() => {
    if (!isLoading && needsEnrichment && !enriching && enrichTrigger < 3) {
      setEnriching(true);
      apiRequest("POST", "/api/logs/enrich", {})
        .then(r => r.json())
        .then((data) => {
          if (data.enriched > 0) {
            queryClient.invalidateQueries({ queryKey: ["/api/logs"] });
          }
          setEnriching(false);
          if (data.remaining > 0) {
            setEnrichTrigger(prev => prev + 1);
          }
        })
        .catch(() => setEnriching(false));
    }
  }, [isLoading, needsEnrichment, enrichTrigger]);

  useEffect(() => {
    if (!isDateKey(requestedDay)) return;
    setExpandedDays(prev => {
      if (prev.has(requestedDay)) return prev;
      return new Set([...prev, requestedDay]);
    });
  }, [requestedDay]);

  const grouped: Record<string, Log[]> = {};
  logs.forEach(log => {
    const key = formatDateKey(log.loggedAt, configuredTimeZone);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(log);
  });

  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  const toggleDay = (date: string) => {
    const isCollapsing = expandedDays.has(date);
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
    if (isCollapsing && requestedDay === date) {
      navigate("/entries", { replace: true });
    } else if (!isCollapsing) {
      navigate(`/entries?day=${encodeURIComponent(date)}`, { replace: true });
    }
  };

  if (entryId !== undefined) {
    if (isLoading) {
      return (
        <AtmosphericPage
          contained
          atmosphere={{ scrimVariant: "content" }}
          className="overflow-hidden"
          contentClassName="flex h-full items-center justify-center"
          testId="entries-detail-loading"
          data-time-zone={configuredTimeZone}
        >
          <div className="flex gap-1.5" aria-label="Loading entry">
            {[0, 1, 2, 3].map(i => (
              <div
                key={i}
                className="w-2 h-2 rounded-full"
                style={{
                  backgroundColor: "var(--accent-text-soft)",
                  animation: `gentle-pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
                }}
              />
            ))}
          </div>
        </AtmosphericPage>
      );
    }

    const parsedEntryId = Number(entryId);
    const detailLog = Number.isSafeInteger(parsedEntryId)
      ? logs.find(log => log.id === parsedEntryId)
      : undefined;

    if (isError && !detailLog) {
      return (
        <AtmosphericPage
          contained
          atmosphere={{ scrimVariant: "content" }}
          className="overflow-hidden"
          contentClassName="flex h-full items-center justify-center px-6"
          testId="entries-detail-error"
          data-time-zone={configuredTimeZone}
        >
          <div className="max-w-sm text-center" role="alert">
            <h1
              className="type-display text-xl"
              style={{ color: "var(--fg-raw)" }}
            >
              Entry unavailable
            </h1>
            <p
              className="type-reflective mt-2"
              style={{ color: "var(--text-secondary)", lineHeight: 1.7 }}
            >
              Fractal could not load this entry. Your place is still here.
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="type-label mt-5 rounded-lg px-4 py-2"
              style={{
                color: "var(--accent-color)",
                background: "var(--accent-bg)",
                border: "1px solid var(--accent-border)",
              }}
              data-testid="button-retry-entry"
            >
              TRY AGAIN
            </button>
          </div>
        </AtmosphericPage>
      );
    }

    if (!detailLog) {
      const returnPath = isDateKey(requestedFromDay)
        ? `/entries?day=${encodeURIComponent(requestedFromDay)}`
        : "/entries";
      return <Redirect to={returnPath} replace />;
    }

    const detailDateKey = formatDateKey(detailLog.loggedAt, configuredTimeZone);
    const openedFromEntriesList = window.history.state?.fromEntriesList === true;
    return (
      <AtmosphericPage
        contained
        atmosphere={{ scrimVariant: "content" }}
        className="mirror-atmosphere-page--scrollable"
        contentClassName="flex-1"
        testId="entries-detail-page"
        data-time-zone={configuredTimeZone}
      >
        <div className="max-w-2xl mx-auto px-6 py-6 pb-24">
          <EntryDetail
            log={detailLog}
            dateKey={detailDateKey}
            timeZone={configuredTimeZone}
            onBack={() => {
              if (openedFromEntriesList) {
                window.history.back();
                return;
              }
              navigate(
                `/entries?day=${encodeURIComponent(detailDateKey)}`,
                { replace: true },
              );
            }}
            onSave={(id, data) => updateMutation.mutate({ id, data })}
            isSaving={updateMutation.isPending}
          />
        </div>
      </AtmosphericPage>
    );
  }

  return (
    <AtmosphericPage
      contained
      atmosphere={{ scrimVariant: "content" }}
      className="overflow-hidden"
      contentClassName="flex min-h-0 flex-col h-full"
      testId="entries-list-page"
      data-time-zone={configuredTimeZone}
    >
      <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid var(--border-medium)" }}>
        <div>
          <h1
            className="text-lg"
            style={{ fontFamily: "var(--font-display)", color: "var(--fg-raw)" }}
            data-testid="text-entries-heading"
          >
            Entries
          </h1>
          <p className="mt-0.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-display)" }}>
            {logs.length} {logs.length === 1 ? "entry" : "entries"} across {sortedDates.length} {sortedDates.length === 1 ? "day" : "days"}
          </p>
        </div>
        {enriching && (
          <div className="flex items-center gap-2" data-testid="enriching-indicator">
            <RefreshCw size={12} className="animate-spin" style={{ color: "var(--accent-text-soft)" }} />
            <span className="font-mono text-xs" style={{ color: "var(--accent-text-soft)" }}>
              Analyzing...
            </span>
          </div>
        )}
      </div>

      <div className="mirror-scroll-region flex-1" data-testid="entries-scroll-region">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3" data-testid="entries-loading">
            <div className="flex gap-1.5">
              {[0, 1, 2, 3].map(i => (
                <div key={i} className="w-2 h-2 rounded-full" style={{ backgroundColor: "var(--accent-text-soft)", animation: `gentle-pulse 1.4s ease-in-out ${i * 0.2}s infinite` }} />
              ))}
            </div>
          </div>
        ) : sortedDates.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-80 gap-5 px-8 text-center">
            <div className="font-serif text-3xl leading-tight" style={{ fontWeight: 400 }} data-testid="text-no-entries">
              Nothing yet
            </div>
            <div className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)", fontFamily: "var(--font-reflective)" }}>
              Talk to Fractal and entries will appear here automatically.
              <br />Or capture a moment yourself.
            </div>
          </div>
        ) : (
          <div className="pb-8 p-4 flex flex-col" style={{ gap: "32px" }}>
            {(() => {
              const monthGroups: Record<string, string[]> = {};
              sortedDates.forEach(date => {
                const key = formatDateKeyLabel(date, { month: "long", year: "numeric" });
                if (!monthGroups[key]) monthGroups[key] = [];
                monthGroups[key].push(date);
              });
              return Object.entries(monthGroups).map(([monthYear, dates]) => (
                <div key={monthYear}>
                  <div className="mb-3">
                    <span
                      className="tracking-wider uppercase"
                      style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: "var(--type-label-size)", fontWeight: 500, letterSpacing: "var(--type-label-tracking)", textTransform: "uppercase" }}
                    >
                      {monthYear}
                    </span>
                  </div>

                  <div className="flex flex-col" style={{ gap: "20px" }}>
                    {dates.map(date => {
                      const todayStr = formatDateKey(new Date(), configuredTimeZone);
                      const yesterdayStr = previousDateKey(todayStr);
                      const isToday = date === todayStr;
                      const isYesterday = date === yesterdayStr;
                      const dayLogs = grouped[date];
                      const isExpanded = expandedDays.has(date);
                      const dominantMood = dayLogs.reduce<Record<string, number>>((acc, l) => {
                        acc[l.mood] = (acc[l.mood] || 0) + 1;
                        return acc;
                      }, {});
                      const topMood = Object.entries(dominantMood).sort((a, b) => b[1] - a[1])[0]?.[0] || "calm";
                      const day = String(Number(date.slice(8, 10)));
                      const weekday = formatDateKeyLabel(date, { weekday: "long" });

                      return (
                        <MirrorCard
                          key={date}
                          className="fade-in overflow-hidden"
                          testId={`day-group-${date}`}
                        >
                          <button
                            className="w-full flex items-center justify-between py-1 transition-all"
                            onClick={() => toggleDay(date)}
                            data-testid={`button-expand-day-${date}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className="flex flex-col items-center">
                                <span
                                  className="text-xl leading-none"
                                  style={{ fontFamily: "var(--font-display)", color: "var(--fg-raw)" }}
                                >
                                  {day}
                                </span>
                                <span
                                  className="mt-0.5"
                                  style={{ fontFamily: "var(--font-mono)", fontSize: "var(--type-label-size)", fontWeight: 500, color: "var(--text-muted)", letterSpacing: "var(--type-label-tracking)", textTransform: "uppercase" }}
                                >
                                  {weekday.slice(0, 3).toUpperCase()}
                                </span>
                              </div>

                              <div className="text-left">
                                <span
                                  className="capitalize"
                                  style={{
                                     color: "var(--accent-color)",
                                    fontFamily: "var(--font-mono)",
                                    fontSize: "var(--type-label-size)",
                                    fontWeight: 500,
                                    letterSpacing: "var(--type-label-tracking)",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {topMood}
                                </span>
                                <p
                                  className="mt-0.5"
                                  style={{
                                    color: "var(--text-muted)",
                                    fontFamily: "var(--font-reflective)",
                                    fontSize: "0.9375rem",
                                    fontWeight: 400,
                                  }}
                                >
                                  {dayLogs.length} {dayLogs.length === 1 ? "entry" : "entries"}
                                  {isToday ? " · today" : isYesterday ? " · yesterday" : ""}
                                </p>
                              </div>
                            </div>

                            <ChevronDown
                              size={16}
                              style={{
                                color: "var(--text-muted)",
                                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.2s ease",
                              }}
                            />
                          </button>

                          {isExpanded && (
                            <div
                              className="fade-in"
                              style={{ borderTop: "1px solid var(--border-light)", marginTop: "12px" }}
                              data-testid={`day-entries-${date}`}
                            >
                              <div className="py-2">
                                {dayLogs.map(log => (
                                  <EntryPreview
                                    key={log.id}
                                    log={log}
                                    timeZone={configuredTimeZone}
                                    onClick={() => navigate(
                                      `/entries/${log.id}?from=${encodeURIComponent(date)}`,
                                      { state: { fromEntriesList: true } },
                                    )}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </MirrorCard>
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
          </div>
        )}
      </div>
    </AtmosphericPage>
  );
}
